/**
 * Pure cache surgery for the "For you" section's `eventHelpableKey(eventId)`
 * entry (spec 3.2). Split out of ForYouSection so the optimistic-dismiss
 * arithmetic (the guard + the composable rollback) is unit-testable without a
 * render, and so the component file keeps a single component export (fast-refresh).
 */
import type { EventHelpableResponse } from '@models/api';

/**
 * Optimistic removal of one card: drop the item AND decrement the pre-cap
 * `total` - but ONLY when the item is actually present. A concurrent
 * focus-refetch can retire the match first, and decrementing a count we did not
 * reduce would undercount (mirrors the base hook's affected-only badge decrement
 * in `useDismissHelpable`). Returns the SAME reference when the item is already
 * gone, so React Query skips a needless notify.
 */
export function removeHelpableItem(
  response: EventHelpableResponse,
  questionId: string,
): EventHelpableResponse {
  if (!response.items.some(item => item.question_id === questionId)) {
    return response;
  }
  return {
    ...response,
    items: response.items.filter(item => item.question_id !== questionId),
    total: Math.max(0, response.total - 1),
  };
}

/**
 * Composable rollback of ONE optimistic removal: re-insert the dismissed item
 * from its `previous` snapshot into the CURRENT cache at its original index,
 * incrementing `total`. Composing over `current` (not clobbering with the whole
 * `previous` snapshot) is what keeps a rapid second dismiss intact: two dismisses
 * each snapshot `previous` AFTER the other's write, so restoring a full snapshot
 * on the first's failure would resurrect a sibling the second already removed.
 * No-op when we never held the item, or it is already back.
 */
export function restoreHelpableItem(
  current: EventHelpableResponse | undefined,
  previous: EventHelpableResponse | undefined,
  questionId: string,
): EventHelpableResponse | undefined {
  if (!current) {
    return previous;
  }
  const index = previous?.items.findIndex(item => item.question_id === questionId) ?? -1;
  const dropped = index >= 0 ? previous?.items[index] : undefined;
  if (!dropped || current.items.some(item => item.question_id === questionId)) {
    return current;
  }
  const items = [...current.items];
  items.splice(Math.min(index, items.length), 0, dropped);
  return { ...current, items, total: current.total + 1 };
}
